import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Search, Clock, Eye, ArrowRight } from 'lucide-react';
import { blogPosts, blogCategories } from '../data/mock';
import { staggerCards } from '../utils/animations';

gsap.registerPlugin(ScrollTrigger);

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All Posts');
  const [searchTerm, setSearchTerm] = useState('');
  const headerRef = useRef(null);
  const postsRef = useRef(null);

  useEffect(() => {
    // Header animation
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, stagger: 0.2, duration: 1, ease: 'power3.out' }
      );
    }

    // Blog posts animation
    if (postsRef.current) {
      const cards = postsRef.current.querySelectorAll('.blog-card');
      staggerCards(cards);
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [selectedCategory, searchTerm]);

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All Posts' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="blog-page pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <div ref={headerRef} className="text-center">
          <span className="inline-block px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
            Insights & Knowledge
          </span>
          <h1 className="font-headline text-5xl md:text-7xl font-bold tracking-headline uppercase mb-8">
            Tech <span className="text-[var(--primary)]">Blog</span>
          </h1>
          <p className="font-body text-[var(--on-surface-variant)] text-xl max-w-3xl mx-auto leading-relaxed mb-12">
            Expert insights on software development, AI, cloud computing,
            <br />
            and the latest in technology innovation
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--on-surface-variant)]" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[var(--surface-container)] border border-[var(--outline-variant)]/20 focus:border-[var(--primary)] outline-none px-12 py-4 font-body text-[var(--on-surface)] transition-colors duration-300 rounded-sm"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-16">
        <div className="flex flex-wrap justify-center gap-3">
          {blogCategories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 font-headline text-xs font-bold uppercase tracking-code transition-all rounded-sm ${
                selectedCategory === category
                  ? 'bg-[var(--primary)] text-[var(--on-primary)] hover:brightness-110'
                  : 'bg-[var(--surface-container)] text-[var(--on-surface-variant)] hover:text-[var(--primary)]'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section ref={postsRef} className="max-w-7xl mx-auto px-6 lg:px-8 mb-32">
        {filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="blog-card group bg-[var(--surface-container-low)] hover:bg-[var(--surface-container-high)] transition-all duration-500 overflow-hidden"
              >
                {/* Image */}
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Category & Read Time */}
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-[var(--primary)] font-body text-xs tracking-code uppercase">
                      {post.category}
                    </span>
                    <div className="flex items-center gap-1 text-[var(--on-surface-variant)] text-xs">
                      <Clock size={14} />
                      {post.readTime} min
                    </div>
                    <div className="flex items-center gap-1 text-[var(--on-surface-variant)] text-xs">
                      <Eye size={14} />
                      {post.views}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-headline text-xl font-bold tracking-tight mb-3 line-clamp-2 group-hover:text-[var(--primary)] transition-colors">
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="font-body text-[var(--on-surface-variant)] text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Author & Date */}
                  <div className="flex items-center justify-between pt-4 border-t border-[var(--outline-variant)]/10">
                    <div>
                      <p className="font-headline text-xs font-bold">{post.author}</p>
                      <p className="font-body text-[var(--on-surface-variant)] text-xs">{post.authorRole}</p>
                    </div>
                    <ArrowRight size={20} className="text-[var(--primary)] group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>

                {/* Top border on hover */}
                <div className="absolute top-0 left-0 w-full h-1 bg-[var(--primary)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="font-body text-[var(--on-surface-variant)] text-lg">
              No articles found matching your criteria
            </p>
          </div>
        )}
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-[var(--surface-container-lowest)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-headline text-4xl md:text-5xl font-bold tracking-headline uppercase mb-6">
            Stay <span className="text-[var(--primary)]">Updated</span>
          </h2>
          <p className="font-body text-[var(--on-surface-variant)] text-lg mb-8">
            Get the latest insights delivered to your inbox every week
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="your@email.com"
              className="flex-1 bg-[var(--surface-container)] border border-[var(--outline-variant)]/20 focus:border-[var(--primary)] outline-none px-6 py-3 font-body text-[var(--on-surface)] transition-colors duration-300 rounded-sm"
            />
            <button className="magnetic bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-8 py-3 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 transition-all rounded-none">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
